import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { Activity, MousePointerClick } from 'lucide-react';
interface SlideFlywheelProps {
  onContinue: () => void;
}
// Slide 6 — Campaign Learning Curve
// Two metrics only: Engagement & CTA. Rx moves to its own slide.
export function SlideFlywheel({
  onContinue
}: SlideFlywheelProps) {
  const [intensity, setIntensity] = useState(2); // 1 conservative, 2 realistic, 3 ambitious
  // Total lift in percentage points after 4 campaigns
  const lifts = {
    1: {
      engagement: 1,
      cta: 1,
      label: 'Conservative'
    },
    2: {
      engagement: 3,
      cta: 3,
      label: 'Realistic'
    },
    3: {
      engagement: 6,
      cta: 6,
      label: 'Ambitious'
    }
  } as const;
  const current = lifts[intensity as 1 | 2 | 3];
  const metrics = [{
    key: 'engagement',
    icon: <Activity className="w-5 h-5" data-id="element-201" />,
    label: 'Engagement rate',
    base: 15,
    lift: current.engagement,
    caption: 'HCPs who interact with the campaign'
  }, {
    key: 'cta',
    icon: <MousePointerClick className="w-5 h-5" data-id="element-202" />,
    label: 'CTA conversion',
    base: 8,
    lift: current.cta,
    caption: 'Clicks, scans, meeting requests, content opens'
  }];
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-7xl mx-auto" data-id="element-203">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-204">
        <span className="text-teal-400 text-xs uppercase tracking-widest font-medium" data-id="element-205">
          The Campaign Learning Curve
        </span>
        <h2 className="text-3xl md:text-5xl font-serif text-white mt-3 mb-3" data-id="element-206">
          Every campaign gives us data.{' '}
          <span className="text-teal-400" data-id="element-207">We use it to lift the next one.</span>
        </h2>
        <p className="text-base text-white/60 max-w-3xl" data-id="element-208">
          Same reach. Same budget. But each campaign teaches us what works,
          where, for whom, and which next step HCPs are most likely to take.
        </p>
      </motion.div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-8 mt-10 items-center" data-id="element-209">
        {/* LEFT — Slider + info */}
        <div className="lg:col-span-4 space-y-4" data-id="element-210">
          <div className="bg-navy-900/70 p-6 rounded-2xl border border-navy-600 space-y-5" data-id="element-211">
            <div data-id="element-212">
              <p className="text-white/40 text-[10px] uppercase tracking-widest font-medium mb-2" data-id="element-213">
                How aggressively we apply the data
              </p>
              <div className="text-3xl font-serif text-teal-400" data-id="element-214">
                {current.label}
              </div>
            </div>

            <input type="range" min="1" max="3" step="1" value={intensity} onChange={e => setIntensity(Number(e.target.value))} className="w-full accent-teal-400" data-id="element-215" />
            <div className="flex justify-between text-[10px] text-white/40 font-mono" data-id="element-216">
              <span data-id="element-217">Conservative</span>
              <span data-id="element-218">Realistic</span>
              <span data-id="element-219">Ambitious</span>
            </div>
          </div>

          <div className="bg-navy-800/40 border border-navy-700 rounded-xl p-4" data-id="element-220">
            <p className="text-white/40 text-[10px] uppercase tracking-widest font-medium mb-3" data-id="element-221">
              What feeds the loop
            </p>
            <ul className="space-y-2 text-white/65 text-xs leading-relaxed" data-id="element-222">
              <li className="flex items-start gap-2" data-id="element-223">
                <span className="text-teal-400 mt-0.5" data-id="element-224">·</span>
                Quiz answers reveal knowledge gaps
              </li>
              <li className="flex items-start gap-2" data-id="element-225">
                <span className="text-teal-400 mt-0.5" data-id="element-226">·</span>
                Country performance shapes channel mix
              </li>
              <li className="flex items-start gap-2" data-id="element-227">
                <span className="text-teal-400 mt-0.5" data-id="element-228">·</span>
                CTA behaviour refines messaging
              </li>
              <li className="flex items-start gap-2" data-id="element-229">
                <span className="text-teal-400 mt-0.5" data-id="element-230">·</span>
                Each campaign improves the next brief
              </li>
            </ul>
          </div>
        </div>

        {/* RIGHT — Two outcome metrics */}
        <div className="lg:col-span-8 space-y-4" data-id="element-231">
          {metrics.map((row, idx) => {
          const newValue = row.base + row.lift;
          const maxScale = row.base * 2;
          const basePct = row.base / maxScale * 100;
          const newPct = newValue / maxScale * 100;
          return <motion.div key={row.key} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: idx * 0.1
          }} className="bg-navy-900/50 border border-navy-700 rounded-2xl p-5" data-id="element-232">
                <div className="flex items-center justify-between mb-3" data-id="element-233">
                  <div className="flex items-center gap-3" data-id="element-234">
                    <div className="w-10 h-10 rounded-lg bg-teal-400/10 border border-teal-400/20 flex items-center justify-center text-teal-400" data-id="element-235">
                      {row.icon}
                    </div>
                    <div data-id="element-236">
                      <div className="text-white font-medium" data-id="element-237">{row.label}</div>
                      <div className="text-white/40 text-[11px]" data-id="element-238">
                        {row.caption}
                      </div>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-239">
                    <div className="flex items-baseline gap-2" data-id="element-240">
                      <span className="text-white/40 line-through text-sm" data-id="element-241">
                        {row.base}%
                      </span>
                      <motion.span key={newValue} initial={{
                    opacity: 0,
                    y: 4
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} className="text-3xl md:text-4xl font-serif text-teal-400" data-id="element-242">
                        {newValue}%
                      </motion.span>
                    </div>
                    <div className="text-teal-300/70 text-[10px] uppercase tracking-widest font-medium" data-id="element-243">
                      +{row.lift} pts after 4 campaigns
                    </div>
                  </div>
                </div>

                {/* Comparison bar */}
                <div className="relative h-2 bg-navy-800 rounded-full overflow-hidden" data-id="element-244">
                  <div className="absolute top-0 left-0 h-full bg-white/15" style={{
                width: `${basePct}%`
              }} data-id="element-245" />
                  <motion.div key={`${row.key}-${newPct}`} initial={{
                width: `${basePct}%`
              }} animate={{
                width: `${newPct}%`
              }} transition={{
                duration: 0.6,
                ease: 'easeOut'
              }} className="absolute top-0 left-0 h-full bg-gradient-to-r from-teal-500 to-teal-400 rounded-full" data-id="element-246" />
                </div>
              </motion.div>;
        })}

          <p className="text-white/60 text-sm border-l-2 border-teal-400/40 pl-3 py-1" data-id="element-247">
            The first campaign creates engagement.{' '}
            <span className="text-white font-medium" data-id="element-248">
              The next campaigns create advantage —
            </span>{' '}
            because the brief gets smarter every time.
          </p>
        </div>
      </div>

      <div className="flex justify-end mt-6" data-id="element-249">
        <Button onClick={onContinue} data-id="element-250">Show annual lift →</Button>
      </div>
    </div>;
}